import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/redux'
import { DashboardsAPI } from '@/API/Dashboards'
import { loadChatSession } from '@/services/DashboardChatService'
import { fetchDashboardDomainsThunk } from '@/services/DashboardDomainRegistryService'
import { chatSessionActions } from '@/redux/slice/chatSessionSlice'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { dashboardsActions } from '@/redux/slice/dashboardsSlice'
import type { SyncCreationDataReadyPayload } from '@/types/Dashboards.types'
import { DashboardSocketService } from '@/services/DashboardSocketService'

export interface DashboardCreationBootstrapResult {
  dashboardId: string | null
  isCreating: boolean
  mountError: string | null
  handleRetry: () => void
  handleSessionRetry: () => void
}

/**
 * useDashboardCreationBootstrap — encapsulates the side effects for the
 * /dashboards/new route AND the /dashboards/:id route (resume path).
 *
 * Two paths:
 *
 * A. No-id path (/dashboards/new — no :id URL param):
 *   1. POST /v1/dashboards with name "Untitled Dashboard" on mount
 *      (guarded against React Strict Mode double-invoke via hasMounted ref)
 *   2. Navigate to /dashboards/:newId with replace immediately after creation
 *      so hard-refresh lands on the correct URL with the record already existing.
 *   3. dispatch loadChatSession(token, dashboardId) to hydrate state.
 *   4. Socket.IO subscription for sync:creation:data_ready and
 *      sync:creation:complete.
 *
 * B. With-id path (/dashboards/:id — URL already has the dashboard id):
 *   - Skip DashboardsAPI.create entirely.
 *   - Seed dashboardId from the URL param.
 *   - dispatch loadChatSession(token, id) to hydrate Redux state from BE.
 *   - No navigation needed — we are already at the correct URL.
 *   - Socket.IO subscription fires once sessionId is hydrated, same as path A.
 *
 * Per TDD §3d.1.A: the creation name is always "Untitled Dashboard".
 * The sync:creation:complete handler navigates to /dashboards/:id with replace —
 * this is the post-panel phase navigation that was previously triggered by the
 * socket event (same semantics as before, just moved here from DashboardNew).
 */
export function useDashboardCreationBootstrap(): DashboardCreationBootstrapResult {
  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const { id: urlDashboardId } = useParams<{ id?: string }>()
  const { token } = useSelector((state: RootState) => state.user)
  const { sessionId } = useSelector((state: RootState) => state.chatSession)

  const [dashboardId, setDashboardId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [mountError, setMountError] = useState<string | null>(null)

  // Prevents double-invoke under React Strict Mode
  const hasMounted = useRef(false)

  const runCreateAndLoad = useCallback(async () => {
    if (!token) return
    dispatch(chatSessionActions.clearSession())
    setIsCreating(true)
    setMountError(null)

    const result = await DashboardsAPI.create(token, { name: 'Untitled Dashboard' })

    if (!result.data) {
      setMountError("We couldn't start your dashboard. Please try again.")
      setIsCreating(false)
      return
    }

    const newDashboardId = result.data.id
    setDashboardId(newDashboardId)
    dispatch(dashboardsActions.setSelectedDashboard(result.data))
    // Seed the creation slice so downstream creation blocks (e.g. PanelsProposedBlock,
    // which gates buildDashboardThunk on state.dashboardCreation.dashboardId) can fire.
    // This uses the slice's purpose-built setDashboardId action — see
    // dashboardCreationSlice.ts §setDashboardId for rationale.
    dispatch(
      dashboardCreationActions.setDashboardId({
        dashboardId: newDashboardId,
        dashboardName: result.data.name ?? 'Untitled Dashboard',
      })
    )
    setIsCreating(false)

    // Navigate to the canonical dashboard URL immediately after creation so the
    // browser URL reflects the dashboard ID before the AI session is established.
    // Using replace: true means the /dashboards/new entry is removed from history
    // so the user's back button goes to the dashboard list, not back to /new.
    navigate(`/dashboards/${newDashboardId}`, { replace: true })

    dispatch(loadChatSession(token, newDashboardId))
    void dispatch(fetchDashboardDomainsThunk(token))
  }, [dispatch, navigate, token])

  const runLoadExisting = useCallback((id: string) => {
    if (!token) return
    // Seed the dashboardId in Redux so downstream guards (e.g. stale-response
    // guard in loadChatSession, build action gates) have the id available.
    setDashboardId(id)
    dispatch(dashboardCreationActions.setDashboardId({ dashboardId: id }))
    dispatch(loadChatSession(token, id))
    void dispatch(fetchDashboardDomainsThunk(token))
  }, [dispatch, token])

  const handleRetry = useCallback(() => {
    hasMounted.current = false
    void runCreateAndLoad()
  }, [runCreateAndLoad])

  const handleSessionRetry = useCallback(() => {
    if (!token || !dashboardId) return
    dispatch(chatSessionActions.clearSession())
    dispatch(loadChatSession(token, dashboardId))
  }, [dispatch, token, dashboardId])

  // Effect 1: bootstrap on mount.
  //   - If :id is in the URL → load existing (skip create).
  //   - Otherwise → create new dashboard (Strict Mode guarded via hasMounted ref).
  // token is set asynchronously after cookie hydration — do not use [] only.
  useEffect(() => {
    if (!token) return
    if (urlDashboardId) {
      // With-id path: already at the right URL — just hydrate state from BE.
      // hasMounted guard is intentionally NOT applied here: if the component
      // remounts (e.g. Strict Mode double-invoke), re-loading is safe because
      // loadChatSession is idempotent (it replaces state, not appends).
      runLoadExisting(urlDashboardId)
      return
    }
    // No-id path: create a new dashboard exactly once.
    if (hasMounted.current) return
    hasMounted.current = true
    void runCreateAndLoad()
  }, [token, urlDashboardId, runCreateAndLoad, runLoadExisting])

  // Effect 2: socket subscription once dashboardId + sessionId are ready
  useEffect(() => {
    if (!token || !dashboardId || !sessionId) return

    DashboardSocketService.connect(dashboardId, token)
    // v1 events (harvest:progress / harvest:awaiting / harvest:complete /
    // harvest:error / sync:domain:*) must be subscribed on the creation
    // page too — the harvest flow runs from /dashboards/new before the
    // user is redirected to /dashboards/:id (Canvas). Without this,
    // backend emits arrive at the connected socket but no handler is
    // registered → modal never transitions out of 'login' state.
    DashboardSocketService.subscribeV1Events(dispatch)

    const handleDataReady = (data: unknown) => {
      const payload = data as SyncCreationDataReadyPayload
      if (
        typeof payload?.dashboardName === 'string' &&
        payload.dashboardName.trim() !== ''
      ) {
        dispatch(
          dashboardsActions.updateDashboardName({
            id: dashboardId,
            name: payload.dashboardName.trim(),
          })
        )
      }
    }

    const handleCreationComplete = () => {
      navigate(`/dashboards/${dashboardId}`, { replace: true })
    }

    DashboardSocketService.on('sync:creation:data_ready', handleDataReady)
    DashboardSocketService.on('sync:creation:complete', handleCreationComplete)

    return () => {
      DashboardSocketService.off('sync:creation:data_ready', handleDataReady)
      DashboardSocketService.off('sync:creation:complete', handleCreationComplete)
      DashboardSocketService.unsubscribeV1Events()
      DashboardSocketService.disconnect()
    }
  }, [token, dashboardId, sessionId, navigate, dispatch])

  return {
    dashboardId,
    isCreating,
    mountError,
    handleRetry,
    handleSessionRetry,
  }
}
